import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const validateEmail = (v) =>
  !v.trim() ? "El email es obligatorio." : EMAIL_RE.test(v.trim()) ? "" : "Introduce un email válido.";

export const validatePassword = (v, min) =>
  !v ? "La contraseña es obligatoria." : min && v.length < min ? `Mínimo ${min} caracteres.` : "";

export const FieldError = ({ id, msg }) =>
  msg ? <p id={id} data-testid={id} className="text-xs text-red-400">{msg}</p> : null;

export const EmailField = ({ value, onChange, error }) => (
  <div className="space-y-1.5">
    <Label htmlFor="email">Email</Label>
    <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder="tu@email.com" aria-invalid={!!error}
      className={error ? "border-red-500/70 focus-visible:ring-red-500/40" : ""} />
    <FieldError id="email-error" msg={error} />
  </div>
);

export const PasswordField = ({ id = "password", label = "Contraseña", value, onChange, error, hint, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} data-testid={`${id === "password" ? "login-password" : id}-input`} type={show ? "text" : "password"}
          autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error}
          className={`pr-10 ${error ? "border-red-500/70 focus-visible:ring-red-500/40" : ""}`} />
        <button type="button" data-testid={`${id}-toggle-visibility`} onClick={() => setShow((s) => !s)}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-500 transition-colors hover:text-[var(--gold)]">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error ? <FieldError id={`${id}-error`} msg={error} /> : hint && <p className="text-xs text-zinc-500">{hint}</p>}
    </div>
  );
};

export const Brand = () => (
  <div className="flex items-center gap-3">
    <img src="/logo192.png" alt="autovos.site" data-testid="brand-logo" className="h-12 w-12 object-contain" />
    <div>
      <p data-testid="brand-title" className="font-display text-xl font-bold leading-none">autovos.site</p>
      <p data-testid="brand-slogan" className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">trabaja menos, gana mas</p>
    </div>
  </div>
);

export const SubmitButton = ({ busy, children, testid = "login-submit-btn" }) => (
  <button type="submit" disabled={busy} data-testid={testid}
    className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--gold)] text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
    {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />} {children}
  </button>
);

export const AuthShell = ({ children, onSubmit, testid = "login-form" }) => (
  <div className="grain flex min-h-[100dvh] items-center justify-center bg-[#09090b] px-4 py-8">
    <form noValidate onSubmit={onSubmit} data-testid={testid} className="rise relative z-10 w-full max-w-sm space-y-5 rounded-2xl border border-white/5 bg-[#111114] p-6 sm:p-8">
      <Brand />
      {children}
    </form>
  </div>
);
